import { Injectable } from '@angular/core';
import { LambdaService } from './lambda.service';

@Injectable({
  providedIn: 'root'
})
export class InfobipSyncService {
  private readonly shopId = 4;
  private readonly storageKey = 'portalAdmin.infobipReport.lastRequestedAt.shop.4';
  private readonly requestIntervalMs = 24 * 60 * 60 * 1000;
  private inFlight?: Promise<void>;

  constructor(private readonly lambdaService: LambdaService) {}

  syncIfDue(): Promise<void> {
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.performSync().finally(() => {
      this.inFlight = undefined;
    });

    return this.inFlight;
  }

  private async performSync(): Promise<void> {
    const requestedAt = new Date();

    if (this.wasRequestedRecently(requestedAt)) {
      return;
    }

    const startDate = new Date(
      requestedAt.getFullYear(),
      requestedAt.getMonth() - 2,
      1
    );
    const endDateExclusive = new Date(
      requestedAt.getFullYear(),
      requestedAt.getMonth(),
      requestedAt.getDate() + 1
    );

    try {
      const response = await this.lambdaService.requestInfobipReport(
        this.shopId,
        this.formatLocalDate(startDate),
        this.formatLocalDate(endDateExclusive)
      );

      if (this.hasPendingStatus(response)) {
        localStorage.setItem(this.storageKey, requestedAt.toISOString());
      }
    } catch {
      // Infobip synchronization must never interrupt portal initialization.
    }
  }

  private wasRequestedRecently(now: Date): boolean {
    try {
      const storedValue = localStorage.getItem(this.storageKey);
      if (!storedValue) {
        return false;
      }

      const storedTime = Date.parse(storedValue);
      if (!Number.isFinite(storedTime)) {
        return false;
      }

      const elapsedTime = now.getTime() - storedTime;
      return elapsedTime >= 0 && elapsedTime < this.requestIntervalMs;
    } catch {
      return false;
    }
  }

  private formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private hasPendingStatus(response: unknown): boolean {
    return typeof response === 'object'
      && response !== null
      && 'status' in response
      && response.status === 'PENDING';
  }
}
